import { useApi } from '@hero/context';
import type {
  UseSubscribeApi,
  UseSubscriptionAddAddonApi,
  UseSubscriptionAdminForceRenewApi,
  UseSubscriptionAdminOverrideApi,
  UseSubscriptionByOrgIdApi,
  UseSubscriptionCancelApi,
  UseSubscriptionChangeIntervalApi,
  UseSubscriptionChangePlanApi,
  UseSubscriptionChangeSeatsApi,
  UseSubscriptionMineApi,
  UseSubscriptionPayApi,
  UseSubscriptionPaymentMethodCheckoutApi,
  UseSubscriptionPaymentMethodDeleteApi,
  UseSubscriptionRemoveAddonApi,
  UseSubscriptionUsageApi,
} from './types';
import { useMutationAPI, useQueryAPI } from '../../utils';

/** Fetch the current org's subscription, or null if on the implicit free tier. */
export const useSubscriptionMineApi: UseSubscriptionMineApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.getMine,
    query: undefined,
    apiConfig,
  });
};

/**
 * Live consumption for the current period — orders metered, fleet
 * headcount, and what the org committed to.
 *
 * Answers for free-tier orgs too (no subscription doc), so the paywall
 * and the checkout can both count against it.
 */
export const useSubscriptionUsageApi: UseSubscriptionUsageApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.getUsage,
    query: undefined,
    apiConfig,
  });
};

/** Admin: fetch a subscription by orgId. Disabled while orgId is undefined. */
export const useSubscriptionByOrgIdApi: UseSubscriptionByOrgIdApi = (orgId, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.adminGet,
    query: { ownerId: orgId, ownerType: 'org' } as never,
    apiConfig,
    isEnabled: () => !!orgId,
  });
};

/** Start a new subscription. Returns subscription + hosted-checkout URL. */
export const useSubscribeApi: UseSubscribeApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.subscribe,
    apiConfig,
  });
};

/**
 * Single user-initiated subscription payment.
 *
 * - Trialing org: returns `checkoutUrl` for hosted Moyasar handoff.
 * - Active / past_due org with saved card: silent token charge for the
 *   Stripe-style proration delta. `charged: true`, no URL.
 * - Downgrade / no money owed: field update only, no Billing.
 *
 * Replaces the `useSubscriptionChange{Plan,Interval,Seats}Api` hooks
 * for all dashboard flows. Those internal hooks remain available for
 * admin-only tooling that must mutate fields without triggering a charge.
 */
export const useSubscriptionPayApi: UseSubscriptionPayApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.pay,
    apiConfig,
  });
};

/**
 * @deprecated Use `useSubscriptionPayApi` in dashboard flows. This hook
 * mutates the plan field without triggering a charge — only correct for
 * admin tooling.
 */
export const useSubscriptionChangePlanApi: UseSubscriptionChangePlanApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.changePlan,
    apiConfig,
  });
};

/**
 * @deprecated Use `useSubscriptionPayApi` in dashboard flows. This hook
 * mutates the interval field without triggering a charge — only correct
 * for admin tooling.
 */
export const useSubscriptionChangeIntervalApi: UseSubscriptionChangeIntervalApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.changeInterval,
    apiConfig,
  });
};

/**
 * @deprecated Use `useSubscriptionPayApi` in dashboard flows. This hook
 * mutates the seat count without triggering a charge — only correct for
 * admin tooling.
 */
export const useSubscriptionChangeSeatsApi: UseSubscriptionChangeSeatsApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.changeSeats,
    apiConfig,
  });
};

export const useSubscriptionAddAddonApi: UseSubscriptionAddAddonApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.addAddon,
    apiConfig,
  });
};

export const useSubscriptionRemoveAddonApi: UseSubscriptionRemoveAddonApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.removeAddon,
    apiConfig,
  });
};

export const useSubscriptionCancelApi: UseSubscriptionCancelApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.cancel,
    apiConfig,
  });
};

export const useSubscriptionAdminOverrideApi: UseSubscriptionAdminOverrideApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.adminOverride,
    apiConfig,
  });
};

export const useSubscriptionAdminForceRenewApi: UseSubscriptionAdminForceRenewApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'subscription',
    apiFn: api.sales.subscription.adminForceRenew,
    apiConfig,
  });
};

export const useSubscriptionPaymentMethodCheckoutApi: UseSubscriptionPaymentMethodCheckoutApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'subscription',
      apiFn: api.sales.subscription.paymentMethodCheckout,
      apiConfig,
    });
  };

export const useSubscriptionPaymentMethodDeleteApi: UseSubscriptionPaymentMethodDeleteApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'subscription',
      apiFn: api.sales.subscription.paymentMethodDelete,
      apiConfig,
    });
  };
